/**
 * ============================================================================
 * ROUTES CONFIGURATION
 * ============================================================================
 * 
 * Defines all application routes
 */

import { Routes, Route } from 'react-router-dom';
import { Suspense } from 'react';
import { ROUTES } from './utils/constants';
import ProtectedRoute from './components/routing/ProtectedRoute';
import PublicRoute from './components/routing/PublicRoute';
import AdminRoute from './components/routing/AdminRoute';
import NotFound from './components/routing/NotFound';
import Spinner from './components/common/Spinner';

// Pages
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import Transactions from './pages/Transactions';
import TransactionDetail from './pages/TransactionDetail';
import CreateTransaction from './pages/CreateTransaction';
import PublicTransactionView from './pages/PublicTransactionView';
import Wallet from './pages/Wallet';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import PrivacySettings from './pages/PrivacySettings';
import PaymentHistory from './pages/PaymentHistory';
import Payment from './pages/Payment';
import PaymentSuccess from './pages/PaymentSuccess';
import Disputes from './pages/Disputes';
import DisputeDetail from './pages/DisputeDetail';
import RaiseDispute from './pages/RaiseDispute';
import Security from './pages/Security';
import Verification from './pages/Verification';
import DeliveryHistory from './pages/DeliveryHistory';
import DeliveryApproval from './pages/DeliveryApproval';
import MarkDelivered from './pages/MarkDelivered';
import Escrows from './pages/Escrows';
import EscrowDetail from './pages/EscrowDetail';
import Links from './pages/Links';
import LinkDetail from './pages/LinkDetail';
import Storage from './pages/Storage';
import Analytics from './pages/Analytics';
import PublicProfileView from './pages/PublicProfileView';
import Notifications from './pages/Notifications';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminTransactions from './pages/admin/AdminTransactions';
import AdminDisputes from './pages/admin/AdminDisputes';
import AdminFinance from './pages/admin/AdminFinance';
import AdminSettings from './pages/admin/AdminSettings';
import AdminNotifications from './pages/admin/AdminNotifications';
import AdminProfile from './pages/admin/AdminProfile';
import AdminUserVerification from './pages/admin/AdminUserVerification';
import AdminEscrows from './pages/admin/AdminEscrows';
import AdminEscrowDetail from './pages/admin/AdminEscrowDetail';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminPayments from './pages/admin/AdminPayments';
import AdminPaymentDetail from './pages/admin/AdminPaymentDetail';
import AdminSecurity from './pages/admin/AdminSecurity';

// New Pages
import DataExport from './components/user/DataExport';
import AccountDeletion from './pages/AccountDeletion';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import HelpCenter from './pages/HelpCenter';
import ContactUs from './pages/ContactUs';
import Pricing from './pages/Pricing';
import UserSearch from './pages/UserSearch';
import HowItWorks from './pages/HowItWorks';
import SystemStatus from './pages/SystemStatus';
import About from './pages/About';

// Auth Pages
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import VerifyOTP from './pages/auth/VerifyOTP';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import SetPassword from './pages/auth/SetPassword';

const RouteLoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Spinner size="lg" />
  </div>
);

/**
 * AppRoutes Component
 * Main routing configuration for the application
 * 
 * Route Organization:
 * 1. Public Routes - No authentication required
 * 2. Auth Routes - Login/Signup (redirects if authenticated)
 * 3. Protected Routes - Requires authentication
 * 4. Admin Routes - Requires admin role
 * 5. 404 - Not Found page
 */
const AppRoutes = () => {
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Routes>
      {/* Public Routes */}
      <Route path={ROUTES.HOME} element={<LandingPage />} />
      <Route path={ROUTES.PUBLIC_TRANSACTION} element={<PublicTransactionView />} />
      <Route path={ROUTES.TERMS} element={<TermsOfService />} />
      <Route path={ROUTES.PRIVACY_POLICY} element={<PrivacyPolicy />} />
      <Route path={ROUTES.HELP} element={<HelpCenter />} />
      <Route path={ROUTES.CONTACT} element={<ContactUs />} />
      <Route path={ROUTES.PRICING} element={<Pricing />} />
      <Route path={ROUTES.HOW_IT_WORKS} element={<HowItWorks />} />
      <Route path={ROUTES.SYSTEM_STATUS} element={<SystemStatus />} />
      <Route path={ROUTES.ABOUT} element={<About />} />

      {/* Auth Routes */}
      <Route
        path={ROUTES.LOGIN}
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path={ROUTES.SIGNUP}
        element={
          <PublicRoute>
            <Signup />
          </PublicRoute>
        }
      />
      <Route
        path={ROUTES.VERIFY_OTP}
        element={
          <PublicRoute>
            <VerifyOTP />
          </PublicRoute>
        }
      />
      <Route
        path={ROUTES.FORGOT_PASSWORD}
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path={ROUTES.RESET_PASSWORD}
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        }
      />
      <Route
        path={ROUTES.SET_PASSWORD}
        element={
          <ProtectedRoute>
            <SetPassword />
          </ProtectedRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path={ROUTES.DASHBOARD}
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.TRANSACTIONS}
        element={
          <ProtectedRoute>
            <Transactions />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.TRANSACTION_DETAIL}
        element={
          <ProtectedRoute>
            <TransactionDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.CREATE_TRANSACTION}
        element={
          <ProtectedRoute>
            <CreateTransaction />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.WALLET}
        element={
          <ProtectedRoute>
            <Wallet />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PROFILE}
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.SETTINGS}
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PRIVACY}
        element={
          <ProtectedRoute>
            <PrivacySettings />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.DATA_EXPORT}
        element={
          <ProtectedRoute>
            <DataExport />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.ACCOUNT_DELETION}
        element={
          <ProtectedRoute>
            <AccountDeletion />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.USER_SEARCH}
        element={
          <ProtectedRoute>
            <UserSearch />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PAYMENT_HISTORY}
        element={
          <ProtectedRoute>
            <PaymentHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PAYMENT}
        element={
          <ProtectedRoute>
            <Payment />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PAYMENT_SUCCESS}
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.DISPUTES}
        element={
          <ProtectedRoute>
            <Disputes />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.DISPUTE_DETAIL}
        element={
          <ProtectedRoute>
            <DisputeDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.RAISE_DISPUTE}
        element={
          <ProtectedRoute>
            <RaiseDispute />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.DELIVERY_HISTORY}
        element={
          <ProtectedRoute>
            <DeliveryHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.MARK_DELIVERED}
        element={
          <ProtectedRoute>
            <MarkDelivered />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.DELIVERY_APPROVAL}
        element={
          <ProtectedRoute>
            <DeliveryApproval />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PUBLIC_PROFILE}
        element={<PublicProfileView />}
      />
      <Route
        path={ROUTES.NOTIFICATIONS}
        element={
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.SECURITY}
        element={
          <ProtectedRoute>
            <Security />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.VERIFICATION}
        element={
          <ProtectedRoute>
            <Verification />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.ESCROWS}
        element={
          <ProtectedRoute>
            <Escrows />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.ESCROW_DETAIL}
        element={
          <ProtectedRoute>
            <EscrowDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.LINKS}
        element={
          <ProtectedRoute>
            <Links />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.LINK_DETAIL}
        element={
          <ProtectedRoute>
            <LinkDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.STORAGE}
        element={
          <ProtectedRoute>
            <Storage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.ANALYTICS}
        element={
          <ProtectedRoute>
            <Analytics />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes - Role-Based Protection */}
      <Route
        path={ROUTES.ADMIN_DASHBOARD}
        element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        }
      />
      <Route
        path={ROUTES.ADMIN_USERS}
        element={
          <AdminRoute>
            <AdminUsers />
          </AdminRoute>
        }
      />
      <Route
        path={ROUTES.ADMIN_TRANSACTIONS}
        element={
          <AdminRoute>
            <AdminTransactions />
          </AdminRoute>
        }
      />
      <Route
        path={ROUTES.ADMIN_DISPUTES}
        element={
          <AdminRoute>
            <AdminDisputes />
          </AdminRoute>
        }
      />
      <Route
        path={ROUTES.ADMIN_FINANCE}
        element={
          <AdminRoute>
            <AdminFinance />
          </AdminRoute>
        }
      />
      <Route
        path={ROUTES.ADMIN_SETTINGS}
        element={
          <AdminRoute>
            <AdminSettings />
          </AdminRoute>
        }
      />
      <Route
        path={ROUTES.ADMIN_NOTIFICATIONS}
        element={
          <AdminRoute>
            <AdminNotifications />
          </AdminRoute>
        }
      />
      <Route
        path={ROUTES.ADMIN_PROFILE}
        element={
          <AdminRoute>
            <AdminProfile />
          </AdminRoute>
        }
      />
      <Route
        path={ROUTES.ADMIN_USER_VERIFICATION}
        element={
          <AdminRoute>
            <AdminUserVerification />
          </AdminRoute>
        }
      />
      <Route
        path={ROUTES.ADMIN_ESCROWS}
        element={
          <AdminRoute>
            <AdminEscrows />
          </AdminRoute>
        }
      />
      <Route
        path={ROUTES.ADMIN_ESCROW_DETAIL}
        element={
          <AdminRoute>
            <AdminEscrowDetail />
          </AdminRoute>
        }
      />
      <Route
        path={ROUTES.ADMIN_ANALYTICS}
        element={
          <AdminRoute>
            <AdminAnalytics />
          </AdminRoute>
        }
      />
      <Route
        path={ROUTES.ADMIN_PAYMENTS}
        element={
          <AdminRoute>
            <AdminPayments />
          </AdminRoute>
        }
      />
      <Route
        path={ROUTES.ADMIN_PAYMENT_DETAIL}
        element={
          <AdminRoute>
            <AdminPaymentDetail />
          </AdminRoute>
        }
      />
      <Route
        path={ROUTES.ADMIN_SECURITY}
        element={
          <AdminRoute>
            <AdminSecurity />
          </AdminRoute>
        }
      />

      {/* 404 - Custom Not Found Page */}
      <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;

// Re-export route components for convenience
export { ProtectedRoute, PublicRoute, AdminRoute };
